import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import {
  Apple,
  Smartphone,
  Share,
  Plus,
  Bell,
  Sparkles,
  Home,
  Monitor,
  Check,
} from "lucide-react";

const Step = ({ num, icon: Icon, title, children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "44px 1fr",
      gap: 20,
      alignItems: "flex-start",
      padding: "20px 0",
      borderBottom: "1px solid var(--border-default)",
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        border: "1px solid var(--brand-accent)",
        color: "var(--brand-accent)",
        display: "grid",
        placeItems: "center",
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 18,
        fontStyle: "italic",
      }}
    >
      {num}
    </div>
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        {Icon && <Icon size={16} strokeWidth={1.5} color="var(--brand-accent)" />}
        <div
          className="font-serif"
          style={{ fontSize: 20, color: "var(--text-primary)", fontWeight: 500 }}
        >
          {title}
        </div>
      </div>
      <div
        className="editorial-body"
        style={{ fontSize: 14.5, color: "var(--text-secondary)", lineHeight: 1.65 }}
      >
        {children}
      </div>
    </div>
  </div>
);

const Benefit = ({ icon: Icon, title, children }) => (
  <div style={{ textAlign: "center", padding: "0 8px" }}>
    <div
      style={{
        width: 52,
        height: 52,
        margin: "0 auto 14px",
        border: "1px solid var(--border-default)",
        background: "var(--bg-alt)",
        display: "grid",
        placeItems: "center",
      }}
    >
      <Icon size={20} strokeWidth={1.3} color="var(--brand-accent)" />
    </div>
    <h3
      className="font-serif"
      style={{ fontSize: 20, color: "var(--text-primary)", marginBottom: 8, fontWeight: 500 }}
    >
      {title}
    </h3>
    <p
      className="editorial-body"
      style={{ fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.6 }}
    >
      {children}
    </p>
  </div>
);

const Install = () => {
  return (
    <div data-testid="install-page" className="app-shell">
      <Navbar />

      {/* MASTHEAD */}
      <div style={{ background: "var(--bg-alt)", borderBottom: "1px solid var(--border-default)" }}>
        <div
          className="container-belle"
          style={{ paddingTop: 130, paddingBottom: 72, textAlign: "center", maxWidth: 880 }}
        >
          <div
            className="overline"
            style={{
              color: "var(--brand-accent)",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "center",
            }}
          >
            <Sparkles size={14} strokeWidth={1.4} /> Bring Le Salon Home With You
          </div>
          <h1
            className="editorial-h1"
            style={{ fontSize: "clamp(40px, 5.5vw, 64px)", marginTop: 18, marginBottom: 22, lineHeight: 1.05 }}
          >
            Le Salon, on<br />your home screen.
          </h1>
          <p
            className="font-serif"
            style={{
              fontSize: 21,
              fontStyle: "italic",
              color: "var(--text-secondary)",
              maxWidth: 640,
              margin: "0 auto",
              lineHeight: 1.5,
            }}
          >
            Add Le Salon to your phone like a proper app — without ever visiting the App Store. A
            private little doorway, always one tap away.
          </p>
        </div>
      </div>

      {/* WHY INSTALL */}
      <div className="container-belle" style={{ paddingTop: 72, paddingBottom: 24, maxWidth: 1040 }}>
        <div className="overline" style={{ color: "var(--brand-accent)", textAlign: "center", marginBottom: 32 }}>
          · · ·  Why install  · · ·
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 36 }}>
          <Benefit icon={Home} title="Fullscreen, no clutter">
            Tap the Le Salon icon → opens like a real app. No browser bars, no distractions.
          </Benefit>
          <Benefit icon={Bell} title="Gentle notifications">
            A soft whisper when there is a new letter, a live broadcast, or a moment worth coming home to.
          </Benefit>
          <Benefit icon={Sparkles} title="Always up to date">
            Le Salon refreshes itself, quietly, in the background — no App Store, no updates to download.
          </Benefit>
        </div>
      </div>

      {/* TWO INSTALL GUIDES */}
      <div
        className="container-belle"
        style={{ paddingTop: 72, paddingBottom: 96, maxWidth: 1100 }}
      >
        <div
          className="overline"
          style={{ color: "var(--brand-accent)", textAlign: "center", marginBottom: 12 }}
        >
          · · ·  Choose your phone  · · ·
        </div>
        <h2
          className="editorial-h2"
          style={{ fontSize: "clamp(28px, 4vw, 44px)", textAlign: "center", marginBottom: 48 }}
        >
          Two minutes. Done forever.
        </h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 40,
          }}
        >
          {/* iPhone */}
          <div
            data-testid="install-iphone-card"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border-default)",
              padding: "40px 36px",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}
            >
              <Apple size={22} strokeWidth={1.4} color="var(--brand-accent)" />
              <h3
                className="editorial-h2"
                style={{ fontSize: 30, margin: 0, fontWeight: 500 }}
              >
                On iPhone or iPad
              </h3>
            </div>
            <p
              className="editorial-body"
              style={{ fontSize: 14, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 24 }}
            >
              For iPhone or iPad — must be in Safari, not Chrome. Apple&apos;s rule, not ours.
            </p>

            <Step num="1" icon={Smartphone} title="Open Safari">
              On your iPhone, open <strong>Safari</strong> — the blue compass icon. (If your phone defaults to Chrome,
              find Safari by swiping down on the home screen and typing "Safari" in the search box.)
            </Step>
            <Step num="2" icon={Home} title="Visit Le Salon">
              Type or paste into the address bar: <strong>lesalon.belleinspiration.com</strong>
            </Step>
            <Step num="3" icon={Share} title="Tap the Share button">
              Look at the bottom of your screen. Tap the <strong>Share icon</strong> — a square with an
              arrow pointing up. A panel slides up.
            </Step>
            <Step num="4" icon={Plus} title="Add to Home Screen">
              Scroll <em>down</em> in that panel past the row of apps. Tap{" "}
              <strong>"Add to Home Screen."</strong> A preview appears showing the Le Salon door icon.
            </Step>
            <Step num="5" icon={Check} title="Tap Add">
              Tap <strong>"Add"</strong> in the top-right corner. Safari closes. Le Salon's beautiful door
              icon now sits on your home screen — tap it any time. 🚪
            </Step>
          </div>

          {/* Android */}
          <div
            data-testid="install-android-card"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border-default)",
              padding: "40px 36px",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}
            >
              <Smartphone size={22} strokeWidth={1.4} color="var(--brand-accent)" />
              <h3
                className="editorial-h2"
                style={{ fontSize: 30, margin: 0, fontWeight: 500 }}
              >
                On Android phone
              </h3>
            </div>
            <p
              className="editorial-body"
              style={{ fontSize: 14, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 24 }}
            >
              For Android — open Chrome. Easiest of all. Almost automatic.
            </p>

            <Step num="1" icon={Smartphone} title="Open Chrome">
              On your Android phone, open <strong>Chrome</strong> (or your usual browser).
            </Step>
            <Step num="2" icon={Home} title="Visit Le Salon">
              Type or paste into the address bar: <strong>lesalon.belleinspiration.com</strong>
            </Step>
            <Step num="3" icon={Sparkles} title="Wait for the install prompt">
              After a few seconds, Chrome shows a soft banner at the bottom: <em>"Install Le Salon."</em>
            </Step>
            <Step num="4" icon={Check} title="Tap Install">
              Tap <strong>"Install"</strong>. Done. Le Salon's door icon appears on your home screen.
            </Step>

            <div
              style={{
                marginTop: 20,
                padding: "16px 18px",
                background: "var(--bg-alt)",
                border: "1px dashed var(--border-default)",
              }}
            >
              <p
                className="editorial-body"
                style={{ fontSize: 13.5, margin: 0, color: "var(--text-secondary)", lineHeight: 1.6 }}
              >
                <strong style={{ color: "var(--brand-accent)" }}>If you don't see the banner:</strong>{" "}
                Tap the ⋮ menu (top-right) → <strong>"Install app"</strong> or <strong>"Add to Home Screen."</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Desktop bonus */}
        <div
          style={{
            marginTop: 40,
            padding: "32px 36px",
            background: "var(--bg-alt)",
            border: "1px solid var(--border-default)",
            display: "grid",
            gridTemplateColumns: "44px 1fr",
            gap: 20,
            alignItems: "center",
          }}
        >
          <Monitor size={32} strokeWidth={1.3} color="var(--brand-accent)" />
          <div>
            <h4
              className="font-serif"
              style={{ fontSize: 22, fontWeight: 500, marginBottom: 6, color: "var(--text-primary)" }}
            >
              Or — on your laptop too.
            </h4>
            <p
              className="editorial-body"
              style={{ fontSize: 14, color: "var(--text-secondary)", margin: 0, lineHeight: 1.6 }}
            >
              In <strong>Chrome</strong> or <strong>Edge</strong>, visit lesalon.belleinspiration.com →
              look for the small <strong>install icon</strong> in the address bar (a screen with a down arrow) → click it.
              Le Salon opens in its own window, like a desktop app. Perfect for slow Sunday reading.
            </p>
          </div>
        </div>
      </div>

      {/* GENTLE NOTE */}
      <div
        style={{
          background: "var(--bg-alt)",
          borderTop: "1px solid var(--border-default)",
          padding: "64px 24px",
        }}
      >
        <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
          <p
            className="font-serif"
            style={{
              fontSize: 22,
              fontStyle: "italic",
              color: "var(--text-secondary)",
              lineHeight: 1.55,
              margin: 0,
            }}
          >
            Once installed, Le Salon updates itself. New letters, new posts, new whispers from the salon
            — they arrive quietly, without you ever having to reinstall. As it should be.
          </p>
          <p
            className="overline"
            style={{ marginTop: 28, opacity: 0.7, letterSpacing: "0.2em" }}
          >
            — Le Salon
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Install;
